import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "electrical-google-maps-colorado",
  metaTitle: "Electrical Google Maps Optimization in Colorado | GBP for Electricians",
  metaDescription:
    "Rank in the Google Map Pack for electrical work across Colorado — GBP optimization, review velocity, and EV-charger service tuning. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Electrical marketing", to: "/electrical-contractor-marketing-colorado" },
  ],
  currentLabel: "Electrical Google Maps Colorado",
  eyebrow: "Electrical Google Maps — Colorado",
  h1: (
    <>
      Electrical <span className="text-brand-gold">Google Maps</span> in Colorado
    </>
  ),
  heroDescription:
    "Own the Map Pack for electrical work in your Colorado service area — because that's where panel upgrades and EV-install jobs start.",
  intro:
    "The Google Map Pack drives most call volume for electricians in Colorado. We build out your Google Business Profile end-to-end — every relevant electrical service, weekly Posts, geo-tagged photos from real installs, Q&A monitoring, and a structured review-velocity system tied to job completion. Most Colorado electricians have 5–10 services listed when they actually offer 20+ — that gap is pure ranking opportunity.",
  definition: {
    term: "Electrical Google Maps optimization in Colorado",
    body: "Electrical Google Maps optimization is the process of ranking your electrical company in the Map Pack for high-intent searches like 'electrician near me', 'panel upgrade Denver', or 'EV charger install Colorado Springs' through GBP optimization, review velocity, and service tuning.",
  },
  whatWeDo: [
    { title: "Full GBP rebuild", body: "Categories (residential, commercial, electric vehicle), every service line, attributes, hours, service area — all optimized." },
    { title: "Weekly GBP Posts", body: "Service highlights, finished installs, EV-charger features, panel upgrade callouts — published weekly." },
    { title: "Geo-tagged install photos", body: "Real Colorado job photos uploaded with proper geo-tags from each neighborhood you serve." },
    { title: "Review velocity tied to job completion", body: "Automated review requests at the right moment after install — tied to ServiceTitan, Housecall Pro, or your CRM." },
    { title: "Q&A monitoring", body: "Seed common electrical questions, monitor for new ones, respond within hours." },
    { title: "Service-area tuning", body: "Adjust service area boundaries based on actual ranking and routing data." },
  ],
  whyThisMarket: [
    {
      heading: "Most electricians under-list services",
      body: "We routinely audit Colorado electrical GBPs with 4–6 services listed when the company actually offers 20+. Each missing service is a missed ranking opportunity for that exact term.",
    },
    {
      heading: "Reviews drive Map Pack ranking faster than ad spend",
      body: "GBP rewards review velocity — recent reviews count more than total count. A Colorado electrician with 60 reviews from the last 90 days will outrank one with 300 from years ago.",
    },
    {
      heading: "EV-charger category is a brand-new ranking opportunity",
      body: "Google added EV charger install as a GBP service. Most Colorado electricians haven't enabled it. Adding it now positions you for the growth curve.",
    },
  ],
  relatedLinks: [
    { to: "/electrical-contractor-marketing-colorado", label: "Electrical contractor marketing — full hub" },
    { to: "/colorado-springs-google-maps", label: "Local SEO overview" },
    { to: "/electrical-seo-colorado", label: "Electrical SEO in Colorado" },
    { to: "/colorado-springs-google-maps", label: "Colorado Springs Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/case-studies", label: "Colorado contractor case studies" },
    { to: "/google-maps", label: "Google Maps optimization (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How fast can I move up in the electrical Map Pack?", a: "Most Colorado electricians see Map Pack movement in 30–60 days. Electrical is less saturated than plumbing/HVAC, so movement can be faster with consistent optimization." },
    { q: "Should I list every electrical service or just the high-margin ones?", a: "List every legitimate service you offer — Google ranks each independently. Even low-margin services drive calls that lead to high-ticket conversations once you're in the truck." },
    { q: "Do I need both Google Maps and Local Services Ads?", a: "They feed each other. LSAs give immediate paid lead flow; GBP optimization compounds organic Map Pack ranking. Companies that run both get the most lead share." },
    { q: "Do you only serve Colorado Springs electricians?", a: "No — we serve electrical contractors across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Electrical Google Maps Optimization in Colorado",
  serviceDescription:
    "Google Business Profile optimization and Map Pack ranking for electrical contractors across Colorado.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/electrical-google-maps-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
